import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency, formatDate, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import { toneForOrderCreditStatus, paymentMethodLabel, promptTrackingDetails, trackingFromOrder, type TrackingForm } from "../../../shared/Utils";

const getAvailableStatusOptions = (status?: string) => {
  const options: Record<string, string[]> = {
    Processing: ["Processing", "Shipped", "Cancelled"],
    Shipped: ["Shipped", "Delivered", "Cancelled"],
    Delivered: ["Delivered", "Return Requested"],
    "Return Requested": ["Return Requested", "Return Approved", "Return Rejected"],
    "Return Approved": ["Return Approved", "Refunded"],
    Cancelled: ["Cancelled", "Refunded"],
  };

  return options[status || ""] || [status || "-"];
};

const statusTone = (status?: string) => {
  if (["Delivered", "Refunded", "Return Approved"].includes(status || "")) return "success";
  if (["Processing", "Shipped", "Return Requested"].includes(status || "")) return "warning";
  if (["Cancelled", "Return Rejected"].includes(status || "")) return "danger";
  return "muted";
};

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isSavingTracking, setIsSavingTracking] = useState(false);
  const [tracking, setTracking] = useState<TrackingForm>(trackingFromOrder());

  const fetchOrderDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);

    try {
      const result = await apiRequest(`/orders/${id}`, "GET");
      if (result.success && result.order) {
        setOrder(result.order);
        setTracking(trackingFromOrder(result.order));
      } else {
        throw new Error(result.message || "Order not found");
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error Loading Order",
        text: error.message || "Unable to fetch order details",
        icon: "error",
      });
      navigate("/orders");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  const updateStatus = async (newStatus: string) => {
    if (!order || order.orderStatus === newStatus) return;

    if (newStatus === "Cancelled") {
      await handleCancelOrder();
      return;
    }

    if (newStatus === "Return Approved" || newStatus === "Return Rejected") {
      await handleProcessReturn(newStatus === "Return Approved" ? "approve" : "reject");
      return;
    }

    if (newStatus === "Refunded") {
      await handleProcessRefund();
      return;
    }

    let reason = "";
    let trackingPayload: Partial<TrackingForm> | undefined;
    if (newStatus === "Return Requested") {
      const response = await Swal.fire({
        title: "Request return?",
        input: "textarea",
        inputLabel: "Return reason",
        inputPlaceholder: "Write reason...",
        showCancelButton: true,
        confirmButtonText: "Request Return",
      });

      if (!response.isConfirmed) return;
      reason = response.value || "Requested by admin";
    }

    if (newStatus === "Shipped" && !order.trackingInfo?.trackingNumber) {
      const trackingResult = await promptTrackingDetails(tracking);
      if (trackingResult === null) return;
      if (trackingResult !== "skipped") {
        trackingPayload = trackingResult;
      }
    }

    setIsUpdating(true);

    try {
      const result = await apiRequest(`/orders/${order._id}`, "PUT", {
        status: newStatus,
        ...(reason && { reason }),
        ...trackingPayload,
      });

      if (result.success) {
        let nextOrder = result.order;

        if (
          newStatus === "Delivered" &&
          nextOrder?.paymentInfo?.method === "cod" &&
          nextOrder?.paymentInfo?.status === "Pending"
        ) {
          const collect = await Swal.fire({
            title: "COD cash collected?",
            text: "Mark this order payment as Completed now?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Mark Collected",
            cancelButtonText: "Keep Pending",
          });

          if (collect.isConfirmed) {
            const paymentResult = await apiRequest(
              `/orders/${order._id}/payment`,
              "PUT",
              { status: "Completed" },
            );
            if (paymentResult.success) nextOrder = paymentResult.order;
          }
        }

        setOrder(nextOrder);
        setTracking(trackingFromOrder(nextOrder));
        Swal.fire({
          title: "Status Updated",
          text: `Order status set to ${newStatus}`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire("Update Failed", error.message || "Failed to update status", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;

    const response = await Swal.fire({
      title: "Cancel Order?",
      text: "Provide a reason for cancelling this order:",
      input: "textarea",
      inputPlaceholder: "Enter reason (e.g., Customer requested, Out of stock...)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Order",
      confirmButtonColor: "#ef4444",
    });

    if (!response.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/cancel`, "PUT", {
        reason: response.value || "Cancelled by admin",
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire("Cancelled", "Order has been cancelled", "success");
      }
    } catch (error: any) {
      Swal.fire("Cancel Failed", error.message || "Order cancellation failed", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProcessReturn = async (action: "approve" | "reject") => {
    if (!order) return;

    const response = await Swal.fire({
      title: action === "approve" ? "Approve Return?" : "Reject Return?",
      text: `Enter admin note for ${action === "approve" ? "approving" : "rejecting"} return:`,
      input: "textarea",
      inputPlaceholder: "Optional note...",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: action === "approve" ? "Approve Return" : "Reject Return",
      confirmButtonColor: action === "approve" ? "#22c55e" : "#ef4444",
    });

    if (!response.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/return`, "PUT", {
        action,
        adminNote: response.value || "",
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire(
          action === "approve" ? "Return Approved" : "Return Rejected",
          `Return request has been ${action === "approve" ? "approved" : "rejected"}.`,
          "success",
        );
      }
    } catch (error: any) {
      Swal.fire("Return Update Failed", error.message || "Action failed", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!order) return;

    const response = await Swal.fire({
      title: "Process Order Refund",
      html: `
        <div style="text-align:left; font-size:14px; color:#475569; margin-bottom:12px;">
          <p><strong>Order Total:</strong> ${formatCurrency(order.totalPrice)}</p>
          <p><strong>Payment Status:</strong> ${order.paymentInfo?.status || "Pending"}</p>
        </div>
      `,
      input: "number",
      inputLabel: "Refund Amount (INR)",
      inputValue: order.refundInfo?.amount || order.totalPrice,
      showCancelButton: true,
      confirmButtonText: "Issue Refund",
      confirmButtonColor: "#9b6c2b",
    });

    if (!response.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/refund`, "PUT", {
        status: "Completed",
        amount: Number(response.value || order.totalPrice),
      });

      if (result.success) {
        setOrder(result.order);
        Swal.fire("Refund Processed", "Refund recorded successfully", "success");
      }
    } catch (error: any) {
      Swal.fire("Refund Failed", error.message || "Failed to process refund", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!order) return;

    const confirm = await Swal.fire({
      title: "Delete Order Permanently?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Order",
    });

    if (!confirm.isConfirmed) return;

    try {
      const result = await apiRequest(`/orders/${order._id}`, "DELETE");
      if (result.success) {
        Swal.fire({
          title: "Deleted",
          text: "Order has been deleted",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        navigate("/orders");
      }
    } catch (error: any) {
      Swal.fire("Delete Failed", error.message || "Order delete failed", "error");
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const handleUpdatePayment = async (status: "Pending" | "Completed" | "Failed") => {
    if (!order || order.paymentInfo?.status === status) return;

    const labels: Record<string, string> = {
      Completed: "Mark COD cash as collected?",
      Pending: "Set COD payment back to Pending?",
      Failed: "Mark COD payment as Failed?",
    };

    const confirm = await Swal.fire({
      title: labels[status],
      text:
        status === "Completed"
          ? `${formatCurrency(order.paymentInfo?.amount || order.totalPrice)} will be marked as received.`
          : "This only updates payment status. Order status will stay the same.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Update Payment",
    });

    if (!confirm.isConfirmed) return;

    setIsUpdating(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/payment`, "PUT", { status });
      if (result.success) {
        setOrder(result.order);
        Swal.fire({
          title: "Payment Updated",
          text: result.message,
          icon: "success",
          timer: 1400,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire("Update Failed", error.message || "Unable to update payment", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveTracking = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!order) return;

    if (!tracking.courierName.trim() || !tracking.trackingNumber.trim()) {
      Swal.fire("Required", "Courier name and tracking number are required.", "warning");
      return;
    }

    if (
      tracking.trackingUrl.trim() &&
      !/^https?:\/\//i.test(tracking.trackingUrl.trim())
    ) {
      Swal.fire("Invalid URL", "Tracking URL must start with http:// or https://", "warning");
      return;
    }

    setIsSavingTracking(true);
    try {
      const result = await apiRequest(`/orders/${order._id}/tracking`, "PUT", {
        courierName: tracking.courierName.trim(),
        trackingNumber: tracking.trackingNumber.trim(),
        trackingUrl: tracking.trackingUrl.trim(),
        estimatedDelivery: tracking.estimatedDelivery || "",
        note: tracking.note.trim(),
      });

      if (result.success) {
        setOrder(result.order);
        setTracking(trackingFromOrder(result.order));
        Swal.fire({
          title: "Tracking Saved",
          text: result.message || "Shipment tracking updated.",
          icon: "success",
          timer: 1400,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      Swal.fire("Save Failed", error.message || "Unable to save tracking", "error");
    } finally {
      setIsSavingTracking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Order Details…</h2>
      </div>
    );
  }

  if (!order) return null;

  const steps = ["Processing", "Shipped", "Delivered"];
  const currentStepIndex = steps.indexOf(order.orderStatus);
  const isTerminalState = ["Cancelled", "Return Requested", "Return Approved", "Return Rejected", "Refunded"].includes(order.orderStatus);

  const statusOptions = getAvailableStatusOptions(order.orderStatus);
  const canUpdate = statusOptions.length > 1;
  const canCancel = !["Delivered", "Cancelled", "Refunded"].includes(order.orderStatus);
  const canProcessReturn = order.orderStatus === "Return Requested" && order.returnRequest?.status === "Requested";
  const canRefund = ["Cancelled", "Return Approved"].includes(order.orderStatus);
  const canEditTracking = !["Cancelled", "Refunded"].includes(order.orderStatus);
  const savedTracking = order.trackingInfo || {};
  const isCodOrder = order.paymentInfo?.method === "cod";
  const canUpdateCodPayment =
    isCodOrder &&
    !["Cancelled", "Refunded"].includes(order.orderStatus) &&
    order.paymentInfo?.status !== "Refunded";
  const paymentStatusTone =
    order.paymentInfo?.status === "Completed"
      ? "success"
      : order.paymentInfo?.status === "Failed"
        ? "danger"
        : "warning";

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/orders" className="btn-sm btn-outline back-link">
            ← Back to Orders List
          </Link>
          <div className="page-eyebrow">Order Reference</div>
          <h1 className="page-title">
            Order #{order.orderCode || order._id?.toUpperCase()}
          </h1>
          <div className="page-sub">
            Placed on {formatDate(order.createdAt)}
          </div>
        </div>

        <div className="page-hdr-right">
          <button type="button" onClick={handlePrintInvoice} className="btn-sm btn-outline">
            Print Invoice
          </button>

          {canUpdate && (
            <select
              className="header-select"
              value={order.orderStatus}
              disabled={isUpdating}
              onChange={(e) => updateStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  Status: {status}
                </option>
              ))}
            </select>
          )}

          <button type="button" onClick={handleDeleteOrder} className="btn-sm btn-danger">
            Delete Order
          </button>
        </div>
      </div>

      <section className="table-card progress-card">
        <h3 className="progress-title">Order Progress Status</h3>

        {!isTerminalState ? (
          <div className="progress-timeline">
            {steps.map((step, idx) => {
              const isCompleted = currentStepIndex >= idx;
              const isCurrent = currentStepIndex === idx;

              return (
                <div key={step} className="progress-step">
                  <div className={`progress-step-dot ${isCompleted ? "is-completed" : ""} ${isCurrent ? "is-current" : ""}`}>
                    {isCompleted ? "✓" : idx + 1}
                  </div>
                  <div className={`progress-step-label ${isCurrent ? "is-current" : ""}`}>
                    {step}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`terminal-banner ${order.orderStatus === "Cancelled" ? "is-danger" : "is-success"}`}>
            <div>
              <span className={`status-badge ${statusTone(order.orderStatus)}`}>
                {order.orderStatus}
              </span>
              <p>
                {order.cancellation?.reason && `Reason: ${order.cancellation.reason}`}
                {order.returnRequest?.reason && `Return Reason: ${order.returnRequest.reason}`}
              </p>
            </div>

            <div className="terminal-actions">
              {canProcessReturn && (
                <>
                  <button type="button" onClick={() => handleProcessReturn("approve")} className="btn-sm btn-success">
                    Approve Return
                  </button>
                  <button type="button" onClick={() => handleProcessReturn("reject")} className="btn-sm btn-danger">
                    Reject Return
                  </button>
                </>
              )}

              {canRefund && (
                <button type="button" onClick={handleProcessRefund} className="btn-sm btn-outline">
                  Process Refund
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Shipment Tracking</h2>
          <p>Courier, AWB number and tracking link for the customer app</p>
        </div>

        {savedTracking.trackingNumber ? (
          <div className="detail-kv-grid">
            <div className="detail-kv-item">
              <label>Courier</label>
              <strong>{savedTracking.courierName || "—"}</strong>
            </div>
            <div className="detail-kv-item">
              <label>Tracking ID</label>
              <strong>{savedTracking.trackingNumber}</strong>
            </div>
            <div className="detail-kv-item">
              <label>ETA</label>
              <strong>
                {savedTracking.estimatedDelivery
                  ? formatDate(savedTracking.estimatedDelivery)
                  : "—"}
              </strong>
            </div>
            <div className="detail-kv-item">
              <label>Track Link</label>
              <strong>
                {savedTracking.trackingUrl ? (
                  <a
                    href={savedTracking.trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="order-code"
                  >
                    Open tracking
                  </a>
                ) : (
                  "—"
                )}
              </strong>
            </div>
          </div>
        ) : null}

        {canEditTracking ? (
          <form onSubmit={handleSaveTracking} className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Courier Name</label>
              <input
                className="admin-input"
                value={tracking.courierName}
                onChange={(e) =>
                  setTracking((current) => ({ ...current, courierName: e.target.value }))
                }
                placeholder="Delhivery, Bluedart, DTDC..."
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Tracking / AWB Number</label>
              <input
                className="admin-input"
                value={tracking.trackingNumber}
                onChange={(e) =>
                  setTracking((current) => ({ ...current, trackingNumber: e.target.value }))
                }
                placeholder="Enter AWB or tracking ID"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Tracking URL</label>
              <input
                className="admin-input"
                value={tracking.trackingUrl}
                onChange={(e) =>
                  setTracking((current) => ({ ...current, trackingUrl: e.target.value }))
                }
                placeholder="https://..."
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Estimated Delivery</label>
              <input
                type="date"
                className="admin-input"
                value={tracking.estimatedDelivery}
                onChange={(e) =>
                  setTracking((current) => ({
                    ...current,
                    estimatedDelivery: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-field admin-status-field">
              <label className="modal-label">Note</label>
              <textarea
                className="admin-input"
                rows={2}
                value={tracking.note}
                onChange={(e) =>
                  setTracking((current) => ({ ...current, note: e.target.value }))
                }
                placeholder="Optional note for this shipment"
              />
            </div>
            <div className="form-field admin-status-field">
              <button type="submit" className="btn-sm btn-primary" disabled={isSavingTracking}>
                {isSavingTracking ? "Saving..." : "Save Tracking"}
              </button>
            </div>
          </form>
        ) : (
          <div className="admin-card-body">
            <p className="table-muted">
              Tracking cannot be edited on cancelled or refunded orders.
            </p>
          </div>
        )}
      </section>

      <div className="detail-grid-3">
        <div className="table-card info-card">
          <h3 className="info-card-title">Customer Information</h3>
          <div className="info-list">
            <p><strong>Name:</strong> {order.user?.name || order.shippingInfo?.fullName || "Guest Customer"}</p>
            <p><strong>Email:</strong> {order.user?.email || "Not Provided"}</p>
            <p><strong>Phone:</strong> {order.shippingInfo?.phoneNo || order.user?.phone || "N/A"}</p>
            {order.user?._id && (
              <p>
                <strong>Account:</strong>{" "}
                <Link to={`/users/${order.user._id}`} className="order-code">
                  View Profile
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="table-card info-card">
          <h3 className="info-card-title">Shipping Details</h3>
          <div className="info-list">
            <p><strong>Recipient:</strong> {order.shippingInfo?.fullName || order.user?.name || "-"}</p>
            <p><strong>Address:</strong> {order.shippingInfo?.address || "-"}</p>
            <p><strong>City / State:</strong> {order.shippingInfo?.city || "-"}, {order.shippingInfo?.state || "-"}</p>
            <p><strong>Pincode / Country:</strong> {order.shippingInfo?.pinCode || "-"}, {order.shippingInfo?.country || "India"}</p>
            <p><strong>Contact Phone:</strong> {order.shippingInfo?.phoneNo || "-"}</p>
          </div>
        </div>

        <div className="table-card info-card">
          <h3 className="info-card-title">Payment Breakdown</h3>
          <div className="info-list">
            <p>
              <strong>Payment Method:</strong> {paymentMethodLabel(order.paymentInfo?.method)}
            </p>
            <p>
              <strong>Payment Status:</strong>{" "}
              <span className={`status-badge ${paymentStatusTone}`}>
                {order.paymentInfo?.status || "Pending"}
              </span>
            </p>
            {order.paidAt ? (
              <p>
                <strong>Paid At:</strong> {formatDate(order.paidAt)}
              </p>
            ) : null}
            <p><strong>Transaction ID:</strong> <small className="table-muted">{order.paymentInfo?.id || (isCodOrder ? "COD" : "N/A")}</small></p>
            <p><strong>Items Price:</strong> {formatCurrency(order.itemsPrice || order.totalPrice)}</p>
            <p><strong>Tax Price:</strong> {formatCurrency(order.taxPrice || 0)}</p>
            <p><strong>Shipping Price:</strong> {formatCurrency(order.shippingPrice || 0)}</p>
            {Number(order.creditPayment?.amount || 0) > 0 && (
              <p className="info-credit">
                <strong>Credit Used:</strong> {formatCurrency(order.creditPayment.amount)}{" "}
                <span className={`status-badge ${toneForOrderCreditStatus(order.creditPayment?.status)}`}>
                  {order.creditPayment?.status || "Outstanding"}
                </span>
                {order.creditPayment?.transaction && (
                  <>
                    {" · "}
                    <Link to={`/credit-history/${order.creditPayment.transaction}`} className="order-code">
                      View Credit Entry
                    </Link>
                  </>
                )}
              </p>
            )}
            <p>
              <strong>External Payment:</strong>{" "}
              {formatCurrency(
                Number(order.paymentInfo?.amount || 0) ||
                  Math.max(Number(order.totalPrice || 0) - Number(order.creditPayment?.amount || 0), 0),
              )}
            </p>
            {Number(order.coupon?.discountAmount || 0) > 0 && (
              <p className="info-discount">
                <strong>Discount Coupon ({order.coupon?.code}):</strong> -{formatCurrency(order.coupon.discountAmount)}
              </p>
            )}
            <hr />
            <p className="info-total">
              <strong>Grand Total:</strong> {formatCurrency(order.totalPrice)}
            </p>
            {canUpdateCodPayment ? (
              <div className="info-card-actions">
                {order.paymentInfo?.status !== "Completed" ? (
                  <button
                    type="button"
                    className="btn-sm btn-success"
                    disabled={isUpdating}
                    onClick={() => void handleUpdatePayment("Completed")}
                  >
                    Mark Cash Collected
                  </button>
                ) : null}
                {order.paymentInfo?.status !== "Pending" ? (
                  <button
                    type="button"
                    className="btn-sm btn-outline"
                    disabled={isUpdating}
                    onClick={() => void handleUpdatePayment("Pending")}
                  >
                    Mark Pending
                  </button>
                ) : null}
                {order.paymentInfo?.status !== "Failed" ? (
                  <button
                    type="button"
                    className="btn-sm btn-danger"
                    disabled={isUpdating}
                    onClick={() => void handleUpdatePayment("Failed")}
                  >
                    Mark Failed
                  </button>
                ) : null}
              </div>
            ) : isCodOrder ? (
              <p className="text-muted-sm">COD payment cannot be changed on this order.</p>
            ) : (
              <p className="text-muted-sm">Online payment status is updated by the gateway.</p>
            )}
          </div>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Order Items ({order.orderItems?.length || 0})</h2>
          <p>Product list included in this order</p>
        </div>

        <div className="orders-table-scroll">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(order.orderItems || []).map((item: any, idx: number) => (
                <tr key={item._id || item.product || idx}>
                  <td>
                    <div className="img-name-cell">
                      <AppImage
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        crossOrigin="anonymous"
                      />
                      <div>
                        <strong>{item.name}</strong>
                        {item.product && (
                          <small className="table-muted">Product ID: {item.product}</small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{formatCurrency(item.price)}</td>
                  <td><strong>x{item.quantity}</strong></td>
                  <td>
                    <strong>{formatCurrency(Number(item.price || 0) * Number(item.quantity || 1))}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(order.returnRequest?.reason || order.refundInfo?.amount || order.cancellation?.reason) && (
        <section className="table-card info-card">
          <h3 className="info-card-title">Return & Refund Summary</h3>
          <div className="detail-grid-3">
            {order.cancellation?.reason && (
              <div>
                <strong>Cancellation Detail:</strong>
                <p className="text-muted">Reason: {order.cancellation.reason}</p>
                <p className="text-muted">Cancelled At: {formatDate(order.cancellation.cancelledAt)}</p>
              </div>
            )}
            {order.returnRequest?.reason && (
              <div>
                <strong>Return Request:</strong>
                <p className="text-muted">Status: {order.returnRequest.status}</p>
                <p className="text-muted">Reason: {order.returnRequest.reason}</p>
                {order.returnRequest.adminNote && <p className="text-muted">Note: {order.returnRequest.adminNote}</p>}
              </div>
            )}
            {order.refundInfo && (
              <div>
                <strong>Refund Information:</strong>
                <p className="text-muted">Status: {order.refundInfo.status || "Pending"}</p>
                <p className="text-muted">Amount: {formatCurrency(order.refundInfo.amount)}</p>
                {order.refundInfo.refundId && <p className="text-muted">Refund ID: {order.refundInfo.refundId}</p>}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="detail-footer">
        {canCancel && (
          <button type="button" onClick={handleCancelOrder} className="btn-sm btn-danger">
            Cancel Order
          </button>
        )}

        <div className="detail-footer-right">
          <Link to="/orders" className="btn-sm btn-outline">
            Close & Return
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
